import React from "react";
import { publicCountryImage } from "@/lib/countryImage";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import CandidateCard from "@/components/CandidateCard";
import HeroSlider from "@/components/HeroSlider";
import StageProgressMini from "@/components/StageProgressMini";
import { ArrowRight, ShieldCheck, Award, Users, Globe2, Clock } from "lucide-react";

export default function Home() {
  const { t } = useTranslation();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: candidates = [] } = useQuery({ queryKey: ["featured"], queryFn: async () => (await api.get("/candidates", { params: { featured: true, limit: 3 } })).data });
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: async () => (await api.get("/countries")).data });

  const stats = settings?.stats || {};
  const STEPS = [
    { n: "01", t: t("home.step1_t"), d: t("home.step1_d") },
    { n: "02", t: t("home.step2_t"), d: t("home.step2_d") },
    { n: "03", t: t("home.step3_t"), d: t("home.step3_d") },
    { n: "04", t: t("home.step4_t"), d: t("home.step4_d") },
  ];

  return (
    <div>
      {/* HERO */}
      <HeroSlider>
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32 w-full">
          <div className="max-w-3xl fade-up">
            <div className="overline text-[#d4a017] mb-3 sm:mb-6 text-[10px] sm:text-xs" data-testid="hero-overline">{t("hero.overline", "Employment · Est. 2013 · Brunei Darussalam")}</div>
            <h1 className="font-serif text-white text-[2rem] leading-[1.1] sm:text-5xl md:text-6xl lg:text-7xl sm:leading-[1.05] tracking-tight" data-testid="hero-title">
              {t("hero.title", settings?.hero_title || "Curating world-class talent for discerning employers")}
            </h1>
            <p className="mt-4 sm:mt-8 text-sm sm:text-lg md:text-xl text-zinc-200 leading-relaxed max-w-2xl">
              {t("hero.subtitle", settings?.hero_subtitle || "")}
            </p>
            <div className="mt-6 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
              <Link to="/catalog" className="btn-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="hero-cta-catalog">
                {t("hero.cta_catalog", "Browse Candidates")} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="px-6 sm:px-7 py-3 rounded-full border border-white/40 text-white hover:bg-white hover:text-zinc-950 transition-colors text-sm font-semibold text-center" data-testid="hero-cta-contact">
                {t("hero.cta_contact", "Speak with our team")}
              </Link>
            </div>
          </div>
        </div>
      </HeroSlider>

      {/* STATS */}
      <section className="border-y border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-12 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6" data-testid="stats-section">
          {[
            { v: stats.placed || 4200, l: t("stats.placed", "Workers Placed"), suffix: "+" },
            { v: stats.employers || 950, l: t("stats.employers", "Employer Partners"), suffix: "+" },
            { v: stats.countries || 4, l: t("stats.countries", "Active Countries") },
            { v: stats.years || 12, l: t("stats.years", "Years of Service") },
          ].map((s) => (
            <div key={s.l} className="border-l-2 border-[#b8860b] pl-3 sm:pl-4">
              <div className="font-serif text-2xl sm:text-4xl md:text-5xl text-zinc-950">{s.v.toLocaleString()}{s.suffix || ""}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-widest text-zinc-800 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LIVE PIPELINE MINI DASHBOARD — real-time stage counter, Non-Formal + Formal split */}
      <StageProgressMini />

      {/* ADVANTAGES */}
      <section className="py-14 sm:py-24 px-5 sm:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 md:items-end mb-10 sm:mb-14">
            <div className="md:col-span-6">
              <div className="overline mb-2 sm:mb-3">{t("home.why_overline", "Why choose us")}</div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-zinc-950 tracking-tight">{t("home.why_title")}</h2>
            </div>
            <p className="md:col-span-6 text-sm sm:text-base text-zinc-900 leading-relaxed">
              {t("home.why_text")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 sm:gap-6">
            {[
              { icon: ShieldCheck, t: t("home.adv1_title"), d: t("home.adv1_desc"), span: "md:col-span-4"},
              { icon: Award,       t: t("home.adv2_title"), d: t("home.adv2_desc"), span: "md:col-span-4"},
              { icon: Users,       t: t("home.adv3_title"), d: t("home.adv3_desc"), span: "md:col-span-4" },
              { icon: Globe2,      t: t("home.adv4_title"), d: t("home.adv4_desc"), span: "md:col-span-6" },
              { icon: Clock,       t: t("home.adv5_title"), d: t("home.adv5_desc"), span: "md:col-span-6" },
            ].map((a) => (
              <div key={a.t} className={`${a.span} bg-white border border-zinc-200 rounded-2xl sm:rounded-3xl p-5 sm:p-8 card-lift`}>
                <a.icon className="text-[#b8860b] mb-3 sm:mb-5" size={22} />
                <div className="font-serif text-lg sm:text-2xl text-zinc-950 mb-1.5 sm:mb-2">{a.t}</div>
                <p className="text-zinc-900 text-[13px] sm:text-sm leading-relaxed">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-14 sm:py-24 px-5 sm:px-6 lg:px-10 bg-zinc-950 text-white relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto relative">
          <div className="overline text-[#d4a017] mb-2 sm:mb-3">{t("home.process_overline", "Recruitment Process")}</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight mb-8 sm:mb-14 max-w-2xl">{t("home.process_title")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="border-t border-zinc-800 pt-4 sm:pt-6">
                <div className="text-[#d4a017] font-serif text-2xl sm:text-3xl mb-2 sm:mb-3">{s.n}</div>
                <div className="text-base sm:text-xl mb-1 sm:mb-2">{s.t}</div>
                <p className="text-zinc-400 text-[12px] sm:text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRIES */}
      <section className="py-14 sm:py-24 px-5 sm:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8 sm:mb-12">
            <div>
              <div className="overline mb-2 sm:mb-3">{t("home.destinations_overline", "Destinations")}</div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-zinc-950 tracking-tight">{t("home.destinations_title")}</h2>
            </div>
            <Link to="/countries" className="hidden md:inline-flex items-center gap-2 text-sm text-[#b8860b] hover:gap-3 transition-all">{t("action.view_all", "View all")} <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            {countries.slice(0, 3).map((c, idx) => (
              <Link
                key={c.id}
                to={`/countries/${c.id}`}
                className={`group relative rounded-2xl sm:rounded-3xl overflow-hidden aspect-[3/4] sm:aspect-[4/5] block card-lift ${idx === 2 ? "col-span-2 md:col-span-1" : ""}`}
                data-testid={`country-card-${c.code}`}
              >
                <img src={publicCountryImage(c)} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 text-white">
                  <div className="overline text-[#d4a017] mb-1 sm:mb-2 text-[10px] sm:text-xs">{c.code}</div>
                  <div className="font-serif text-xl sm:text-3xl">{c.name}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="md:hidden mt-6 text-center">
            <Link to="/countries" className="inline-flex items-center gap-2 text-sm text-[#b8860b]">{t("action.view_all", "View all")} <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* FEATURED CANDIDATES */}
      <section className="py-14 sm:py-24 px-5 sm:px-6 lg:px-10 bg-zinc-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-8 sm:mb-12">
            <div>
              <div className="overline mb-2 sm:mb-3">{t("home.featured_overline", "Featured Candidates")}</div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-zinc-950 tracking-tight">{t("home.featured_title")}</h2>
            </div>
            <Link to="/catalog" className="hidden md:inline-flex items-center gap-2 text-sm text-[#b8860b] hover:gap-3 transition-all" data-testid="home-view-all-candidates">{t("action.view_all", "View catalog")} <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {candidates.slice(0, 3).map((c) => (<CandidateCard key={c.id} candidate={c} />))}
          </div>
          <div className="md:hidden mt-6 text-center">
            <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-[#b8860b]">{t("action.view_all", "View catalog")} <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-24 px-5 sm:px-6 lg:px-10 bg-zinc-950 text-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative">
          <div className="overline text-[#d4a017] mb-3 sm:mb-4">{t("home.cta_overline", "Ready to begin?")}</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight mb-6 sm:mb-8">{t("home.cta_title")}</h2>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <Link to="/catalog" className="btn-gold text-center" data-testid="footer-cta-catalog">{t("action.view_profile", "View Candidates")}</Link>
            <Link to="/contact" className="px-7 py-3 rounded-full border border-white/40 hover:bg-white hover:text-zinc-950 transition-colors text-sm font-semibold text-center">{t("nav.contact", "Contact Us")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
